import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef } from 'react'
import api from 'api/api'
import { Quest } from 'types/quests'
import { QuestId } from 'state/PanelState'
import {
    getCachedQuest,
    getCachedThreadQuest,
    retainCompactThreadQuestTransport,
} from 'state/cache'
import { QUEST_ENTITY_QUERY_POLICY } from 'api/queryPolicies'
import { doesStreamQuestNeedHydration } from './streamQuestHydration'
import { questNeedsAncestorHydration } from 'lib/scrollAncestors'

type Options = {
    cacheOnly?: boolean
    ensureStreamHydration?: boolean
    ensureAncestors?: boolean
    retainThreadSession?: boolean
    skipStreamQueryTouch?: boolean
    deferAnswerHydration?: boolean
}

export default function useGetQuest(id: QuestId | undefined, options: Options = {}) {
    const fullHydrationRetryQuestIdRef = useRef<string | null>(null)
    // Check if quest is a draft.
    const cachedQuest = id
        ? options.retainThreadSession
            ? getCachedThreadQuest(id)
            : getCachedQuest(id)
        : null
    const enabled =
        !options.cacheOnly &&
        !!id &&
        !cachedQuest?.is_draft &&
        !cachedQuest?.localChildQuest &&
        !cachedQuest?.isLocalChildQuest &&
        !cachedQuest?.isLocalRootQuest
    const cachedQuestNeedsStreamHydration =
        !!options.ensureStreamHydration &&
        (!cachedQuest || doesStreamQuestNeedHydration(cachedQuest))
    // Only the show payload carries `ancestors`, so a quest cached from a feed
    // page renders the thread without its ancestor spine until that entry goes
    // stale. The show response always writes the key (an empty array for a root
    // thread), so this settles after one fetch.
    const cachedQuestNeedsAncestors =
        !!options.ensureAncestors && questNeedsAncestorHydration(cachedQuest)
    const needsFreshFetch = cachedQuestNeedsStreamHydration || cachedQuestNeedsAncestors

    const result = useQuery({
        queryKey: ['quest', id],

        queryFn: async ({ signal }) => {
            const quest = id
                ? await api.getQuestQuery(
                      id,
                      {
                          compactThreadAnswers: options.retainThreadSession,
                          skipStreamQueryTouch: options.skipStreamQueryTouch,
                          streamPreview: options.ensureStreamHydration,
                          deferAnswerHydration: options.deferAnswerHydration,
                      },
                      signal,
                  )
                : null
            if (quest) {
                return quest
            } else {
                // Check if we have a local cached quest
                const localQuest = getCachedQuest(id)
                if (localQuest) {
                    // Return local cached quest (if any) if quest is not found.
                    // This sometimes happens when a local quest exists but the server does not have it yet.
                    return localQuest
                }
                // If no quest returned from API and no local cache, it's a 404
                const error = Object.assign(new Error('Quest not found'), { status: 404 })
                throw error
            }
        },

        enabled,
        gcTime: QUEST_ENTITY_QUERY_POLICY.gcTime,
        staleTime: needsFreshFetch ? 0 : QUEST_ENTITY_QUERY_POLICY.staleTime,
        refetchOnMount: needsFreshFetch ? 'always' : true,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        notifyOnChangeProps: ['data', 'dataUpdatedAt', 'error', 'status', 'fetchStatus'],
        structuralSharing: QUEST_ENTITY_QUERY_POLICY.structuralSharing,
        networkMode: 'always',
        meta: {
            // The thread session owns ordered tail membership. Keep the active Quest and its
            // parent ancestry protected, but let only mounted Answer observers protect tail
            // entities so offscreen hydration can be pruned independently.
            protectsQuestAnswerEntities: !options.retainThreadSession,
        },
    })

    useEffect(() => {
        if (!options.retainThreadSession || !id) return
        return retainCompactThreadQuestTransport(String(id))
    }, [id, options.retainThreadSession])

    const quest = useMemo(() => {
        if (result.data?.id === '__PRIVATE__') return result.data
        if (result.data?.id) {
            return options.retainThreadSession
                ? getCachedThreadQuest(result.data.id)
                : getCachedQuest(result.data.id)
        }
        return null
    }, [options.retainThreadSession, result.data, result.dataUpdatedAt])

    useEffect(() => {
        if (!options.retainThreadSession || !id) return
        if (quest?.__knovAnswerHydrationScope !== 'stream-preview') {
            fullHydrationRetryQuestIdRef.current = null
            return
        }
        const questId = String(id)
        if (fullHydrationRetryQuestIdRef.current === questId) return
        fullHydrationRetryQuestIdRef.current = questId

        let cancelled = false
        void (async () => {
            for (let attempt = 0; attempt < 3 && !cancelled; attempt += 1) {
                // Join a fetch already in flight (an instant open primes this same
                // query at click time) instead of cancelling it and asking again.
                await result.refetch({ cancelRefetch: false })
                if (cancelled) return
                const refreshedQuest = getCachedThreadQuest(questId)
                if (refreshedQuest?.__knovAnswerHydrationScope === 'full') return
                if (attempt < 2) {
                    await new Promise(resolve => window.setTimeout(resolve, 400 * 2 ** attempt))
                }
            }
            if (!cancelled && fullHydrationRetryQuestIdRef.current === questId) {
                fullHydrationRetryQuestIdRef.current = null
            }
        })()

        return () => {
            cancelled = true
            if (fullHydrationRetryQuestIdRef.current === questId) {
                fullHydrationRetryQuestIdRef.current = null
            }
        }
    }, [id, options.retainThreadSession, quest?.__knovAnswerHydrationScope, result.refetch])

    // >>> Hydration issue detection at hook level
    if (result.data && !quest) {
        console.warn('>>> useGetQuest: query succeeded but getCachedQuest returned null', {
            questId: id,
            resultDataId: result.data?.id,
        })
    }
    if (quest && !quest.id) {
        console.warn('>>> useGetQuest: quest missing id', { questId: id, quest })
    }

    return {
        ...result,
        quest,
        error: result.error,
    }
}
